/**
 * FlowHub - Register Page
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Form, Input, Button, Alert, message } from 'antd';
import { MailOutlined, LockOutlined, UserOutlined, PhoneOutlined, IdcardOutlined } from '@ant-design/icons';
import { useAuthStore } from '../store/authStore';
import AuthLayout from '../components/AuthLayout';
import { validateCpfCnpjUnique, validatePhoneUnique } from '@/features/client/utils/validation';

interface RegisterFormValues {
  email: string;
  password: string;
  confirmPassword: string;
  fullName: string;
  cpfCnpj: string;
  phone: string;
}

// Componente para máscara de CPF/CNPJ
const CpfCnpjInput = ({ value, onChange, ...props }: any) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\D/g, '');
    let formatted = '';

    if (input.length <= 11) {
      formatted = input
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      formatted = input
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }

    if (onChange) {
      onChange(formatted);
    }
  };

  return (
    <Input
      {...props}
      value={value}
      onChange={handleChange}
      placeholder="000.000.000-00 ou 00.000.000/0000-00"
      maxLength={18}
    />
  );
};

// Componente para máscara de telefone
const PhoneInput = ({ value, onChange, ...props }: any) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\D/g, '');
    let formatted = '';

    if (input.length <= 10) {
      formatted = input
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d{1,4})$/, '$1-$2');
    } else {
      formatted = input
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
    }

    if (onChange) {
      onChange(formatted);
    }
  };

  return (
    <Input
      {...props}
      value={value}
      onChange={handleChange}
      placeholder="(11) 99999-0000"
      maxLength={15}
    />
  );
};

function RegisterPage() {
  const { signUp } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form] = Form.useForm<RegisterFormValues>();

  const handleSubmit = async (values: RegisterFormValues) => {
    try {
      setLoading(true);
      setError(null);
      
      // Limpar erros anteriores dos campos
      form.setFields([
        { name: 'cpfCnpj', errors: [] },
        { name: 'phone', errors: [] },
        { name: 'email', errors: [] },
      ]);

      if (values.password !== values.confirmPassword) {
        setError('As senhas não coincidem');
        setLoading(false);
        return;
      }

      const normalizedEmail = values.email.trim().toLowerCase();
      const normalizedCpfCnpj = values.cpfCnpj.replace(/\D/g, '');
      const normalizedPhone = values.phone.replace(/\D/g, '');

      // IMPORTANTE: Validar CPF/CNPJ único ANTES de criar usuário
      console.log('Starting CPF/CNPJ validation...');
      const cpfValidation = await validateCpfCnpjUnique(normalizedCpfCnpj);
      console.log('CPF/CNPJ validation result:', cpfValidation);
      
      if (!cpfValidation.isValid) {
        const errorMsg = cpfValidation.error || 'CPF/CNPJ já cadastrado em outra conta';
        form.setFields([
          {
            name: 'cpfCnpj',
            errors: [errorMsg],
          },
        ]);
        message.error(errorMsg);
        setLoading(false);
        return; // IMPORTANTE: Interromper aqui
      }

      // IMPORTANTE: Validar telefone único ANTES de criar usuário
      console.log('Starting phone validation...');
      const phoneValidation = await validatePhoneUnique(normalizedPhone);
      console.log('Phone validation result:', phoneValidation);
      
      if (!phoneValidation.isValid) {
        const errorMsg = phoneValidation.error || 'Telefone já cadastrado em outra conta';
        form.setFields([
          {
            name: 'phone',
            errors: [errorMsg],
          },
        ]);
        message.error(errorMsg);
        setLoading(false);
        return; // IMPORTANTE: Interromper aqui
      }

      console.log('Validation passed, calling signUp...');

      // Somente chama signUp se validações passarem
      await signUp(
        normalizedEmail, 
        values.password, 
        values.fullName, 
        'client',
        normalizedCpfCnpj,
        normalizedPhone
      );

      message.success('Conta criada com sucesso!');
      // Router will handle redirect based on user role
    } catch (err: any) {
      console.error('Register error:', err);
      
      // Tratar erros específicos
      const errorMessage = err.message || '';
      
      if (errorMessage.includes('User already registered') || errorMessage.includes('already registered')) {
        const msg = 'Email já cadastrado. Faça login ou use outro email.';
        form.setFields([
          {
            name: 'email',
            errors: [msg],
          },
        ]);
        message.error(msg);
      } else if (errorMessage.includes('CPF/CNPJ já cadastrado') || errorMessage.includes('cpf_cnpj')) {
        const msg = 'CPF/CNPJ já cadastrado em outra conta';
        form.setFields([
          {
            name: 'cpfCnpj',
            errors: [msg],
          },
        ]);
        message.error(msg);
      } else if (errorMessage.includes('Telefone já cadastrado') || errorMessage.includes('phone')) {
        const msg = 'Telefone já cadastrado em outra conta';
        form.setFields([
          {
            name: 'phone',
            errors: [msg],
          },
        ]);
        message.error(msg);
      } else if (errorMessage.includes('duplicate key') || errorMessage.includes('unique constraint') || errorMessage.includes('CPF/CNPJ ou telefone')) {
        const msg = 'CPF/CNPJ ou telefone já cadastrado em outra conta';
        message.error(msg);
        setError(msg);
        // Highlight potentially problematic fields without specific error just to draw attention?
        // Better to check if we can parse which one, but if ambiguous:
        if (form.getFieldValue('cpfCnpj')) {
           form.setFields([{ name: 'cpfCnpj', errors: ['Pode já estar em uso'] }]);
        }
        if (form.getFieldValue('phone')) {
           form.setFields([{ name: 'phone', errors: ['Pode já estar em uso'] }]);
        }
      } else {
        const msg = err.message || 'Erro ao criar conta. Tente novamente.';
        message.error(msg);
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Crie sua conta"
      subtitle="Centralize automações, clientes e integrações em uma única plataforma pensada para operações que escalam."
      features={[
        'Configure em minutos, não em dias',
        'Suporte dedicado durante onboarding',
        'Sem cartão de crédito para começar'
      ]}
      stats={[
        { label: 'Ativação média', value: '12 min' },
        { label: 'Integrações', value: '+20' }
      ]}
    >
      {error && (
        <Alert
          message={error}
          type="error"
          closable
          onClose={() => setError(null)}
          className="mb-6 rounded-xl shadow-lg"
        />
      )}

      <Form<RegisterFormValues>
        name="register"
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        size="large"
        autoComplete="off"
        className="space-y-4"
      >
        <Form.Item
          name="fullName"
          label={<span className="text-slate-700 font-medium">Nome completo</span>}
          rules={[
            { required: true, message: 'Informe seu nome completo' },
            { min: 2, message: 'Informe pelo menos 2 caracteres' },
          ]}
        >
          <Input
            prefix={<UserOutlined className="text-pink-500" />}
            placeholder="Maria Silva"
            autoComplete="name"
            className="rounded-xl h-12 border-slate-200 hover:border-pink-400 focus:border-pink-500"
          />
        </Form.Item>

        <Form.Item
          name="email"
          label={<span className="text-slate-700 font-medium">Email corporativo</span>}
          rules={[
            { required: true, message: 'Informe seu email' },
            { type: 'email', message: 'Digite um email válido' },
          ]}
        >
          <Input
            prefix={<MailOutlined className="text-pink-500" />}
            placeholder="nome@empresa.com"
            autoComplete="email"
            className="rounded-xl h-12 border-slate-200 hover:border-pink-400 focus:border-pink-500"
          />
        </Form.Item>

        <Form.Item
          name="cpfCnpj"
          label={<span className="text-slate-700 font-medium">CPF/CNPJ</span>}
          rules={[
            { required: true, message: 'Informe seu CPF ou CNPJ' },
          ]}
        >
          <CpfCnpjInput
            prefix={<IdcardOutlined className="text-pink-500" />}
            className="rounded-xl h-12 border-slate-200 hover:border-pink-400 focus:border-pink-500"
          />
        </Form.Item>

        <Form.Item
          name="phone"
          label={<span className="text-slate-700 font-medium">Telefone</span>}
          rules={[
            { required: true, message: 'Informe seu telefone' },
          ]}
        >
          <PhoneInput
            prefix={<PhoneOutlined className="text-pink-500" />}
            className="rounded-xl h-12 border-slate-200 hover:border-pink-400 focus:border-pink-500"
          />
        </Form.Item>

        <Form.Item
          name="password"
          label={<span className="text-slate-700 font-medium">Senha</span>}
          rules={[
            { required: true, message: 'Crie uma senha' },
            { min: 8, message: 'Use ao menos 8 caracteres' },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="text-pink-500" />}
            placeholder="••••••••"
            autoComplete="new-password"
            className="rounded-xl h-12 border-slate-200 hover:border-pink-400 focus:border-pink-500"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label={<span className="text-slate-700 font-medium">Confirme a senha</span>}
          dependencies={['password']}
          rules={[
            { required: true, message: 'Confirme sua senha' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('As senhas não coincidem'));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="text-pink-500" />}
            placeholder="••••••••"
            autoComplete="new-password"
            className="rounded-xl h-12 border-slate-200 hover:border-pink-400 focus:border-pink-500"
          />
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          block
          size="large"
          className="!h-12 !rounded-xl !bg-gradient-to-r !from-pink-600 !to-orange-600 hover:!from-pink-700 hover:!to-orange-700 !border-0 !shadow-lg !shadow-pink-500/50 !font-semibold !text-base"
        >
          Criar conta
        </Button>
      </Form>

      <div className="mt-8 text-center text-sm text-slate-600">
        Já possui conta?{' '}
        <Link
          to="/login"
          className="font-semibold text-pink-600 hover:text-orange-600 transition-colors"
        >
          Entrar
        </Link>
      </div>
    </AuthLayout>
  );
}

export default RegisterPage;
